const PAGE_SIZE = 20;

export default {
  data() {
    return {
      selectOptions: [],
      isLoadingOptions: false,
      keyOptions: [],
      valueOptions: [],
      isLoadingKeyOptions: false,
      isLoadingValueOptions: false,
      lastError: null,
    };
  },
  methods: {
    async loadSelectOptions() {
      const config = this.currentFieldConfig;

      if (!config || config.type !== 'select') {
        return;
      }

      if (this.isLoadingOptions) {
        return;
      }

      this.isLoadingOptions = true;
      this.lastError = null;

      try {
        if (config.options) {
          await this.loadStaticOptions(config.options);
        } else if (config.fetchOptions) {
          await this.loadAsyncOptions(config.fetchOptions);
        } else {
          this.selectOptions = [];
        }
      } catch (error) {
        console.error('Error loading select options:', error);
        this.lastError = error.message;

        this.$snack(`${this.$i18n_t('Core.MultiSearch.errors.loadingOptions')}: ${error.message}`, 'error');
        this.selectOptions = [];
      } finally {
        this.isLoadingOptions = false;
      }
    },
    async loadStaticOptions(options) {
      const validOptions = this.validateOptionsFormat(options);
      this.selectOptions = validOptions;
    },
    async loadAsyncOptions(fetchOptions) {
      const category = this.selectedField;
      const state = this.getPaginationState(category);

      try {
        const offset = (state.page - 1) * PAGE_SIZE;
        const fetchedData = await fetchOptions({
          page: state.page,
          offset: offset,
          pageSize: PAGE_SIZE,
          search: state.searchTerm,
        });

        let options = [];
        let hasMore = false;

        if (Array.isArray(fetchedData)) {
          options = fetchedData;
          hasMore = fetchedData.length === PAGE_SIZE;
        } else if (fetchedData && typeof fetchedData === 'object') {
          options = fetchedData.data || fetchedData.items || [];
          hasMore =
            fetchedData.hasMore !== undefined ? fetchedData.hasMore : fetchedData.total > state.page * PAGE_SIZE;
        }

        const validOptions = this.validateOptionsFormat(options);

        if (state.page === 1) {
          this.selectOptions = validOptions;
        } else {
          this.selectOptions = [...this.selectOptions, ...validOptions];
        }

        this.updatePaginationState(category, {
          hasMore,
          isLoadingMore: false,
        });
      } catch (error) {
        this.updatePaginationState(category, {
          hasMore: false,
          isLoadingMore: false,
        });
        throw error;
      }
    },
    validateOptionsFormat(options) {
      if (!Array.isArray(options)) {
        throw new Error('Options must be an array');
      }

      return options.map((option, index) => {
        if (typeof option === 'string') {
          return { value: option, text: option };
        }

        if (typeof option === 'object' && option !== null) {
          const value = option.value !== undefined ? option.value : option.id;
          const text = option.text || option.label || option.name || value;

          if (value === undefined) {
            throw new Error(`Option at index ${index} is missing value/id property`);
          }

          return { value, text };
        }

        throw new Error(`Invalid option format at index ${index}`);
      });
    },
    async loadKeyValueOptions() {
      const config = this.currentFieldConfig;
      if (!config || config.type !== 'keyValue') {
        return;
      }

      await this.loadKeyOptions();

      if (!config.fetchValueOptions) {
        await this.loadValueOptions();
      }
    },
    getKeyFetchMeta() {
      const keyPaginationState = this.getKeyPaginationState ? this.getKeyPaginationState() : null;
      const keyPage = keyPaginationState?.page || 1;
      const searchTerm = keyPaginationState?.searchTerm || '';
      const offset = (keyPage - 1) * PAGE_SIZE;

      return {
        keyPage,
        searchTerm,
        offset,
      };
    },
    extractKeyOptionsAndHasMore(fetchedData, keyPage) {
      let options = [];
      let hasMore = false;

      if (Array.isArray(fetchedData)) {
        options = fetchedData;
        hasMore = fetchedData.length === PAGE_SIZE;
      } else if (fetchedData && typeof fetchedData === 'object') {
        options = fetchedData.entities || fetchedData.data || fetchedData.items || [];
        hasMore =
          fetchedData.hasMore !== undefined ? fetchedData.hasMore : fetchedData.totalCount > keyPage * PAGE_SIZE;
      }

      return {
        options,
        hasMore,
      };
    },
    updateKeyLoadingPagination(hasMore = false) {
      if (this.updateKeyPaginationState) {
        this.updateKeyPaginationState({
          hasMore,
          isLoadingMore: false,
        });
      }
    },
    async loadKeyOptions() {
      const config = this.currentFieldConfig;
      if (!config || config.type !== 'keyValue' || (!config.keyOptions && !config.fetchKeyOptions)) {
        return;
      }

      if (this.isLoadingKeyOptions) {
        return;
      }

      this.isLoadingKeyOptions = true;
      this.lastError = null;

      try {
        if (config.keyOptions) {
          const validOptions = this.validateOptionsFormat(config.keyOptions);
          this.keyOptions = validOptions;
          this.updateKeyLoadingPagination(false);
        } else if (config.fetchKeyOptions) {
          const { keyPage, searchTerm, offset } = this.getKeyFetchMeta();
          const fetchedData = await config.fetchKeyOptions({
            offset,
            limit: PAGE_SIZE,
            search: searchTerm,
          });
          const { options, hasMore } = this.extractKeyOptionsAndHasMore(fetchedData, keyPage);
          const validOptions = this.validateOptionsFormat(options);

          if (keyPage === 1) {
            this.keyOptions = validOptions;
          } else {
            this.keyOptions = [...this.keyOptions, ...validOptions];
          }
          this.updateKeyLoadingPagination(hasMore);
        }
      } catch (error) {
        console.error('Error loading key options:', error);
        this.lastError = error.message;

        this.$snack(`${this.$i18n_t('Core.MultiSearch.errors.loadingKeyOptions')}: ${error.message}`, 'error');

        this.keyOptions = [];
        this.updateKeyLoadingPagination(false);
      } finally {
        this.isLoadingKeyOptions = false;
      }
    },
    async loadValueOptions() {
      const config = this.currentFieldConfig;
      if (!config || config.type !== 'keyValue' || (!config.valueOptions && !config.fetchValueOptions)) {
        return;
      }

      if (this.isLoadingValueOptions) {
        return;
      }

      this.isLoadingValueOptions = true;
      this.lastError = null;

      try {
        if (config.valueOptions) {
          const validOptions = this.validateOptionsFormat(config.valueOptions);
          this.valueOptions = validOptions;
        } else if (config.fetchValueOptions) {
          const selectedKey =
            this.inputValues.key !== null && this.inputValues.key !== undefined
              ? this.inputValues.key.toString().trim()
              : '';
          const fetchedOptions = await config.fetchValueOptions(selectedKey, {
            offset: 0,
            limit: PAGE_SIZE,
            search: this.valueSearchTerm || '',
          });
          const validOptions = this.validateOptionsFormat(fetchedOptions);
          this.valueOptions = validOptions;
        }
      } catch (error) {
        this.lastError = error.message;
        this.$snack(`${this.$i18n_t('Core.MultiSearch.errors.loadingValueOptions')}: ${error.message}`, 'error');
        this.valueOptions = [];
      } finally {
        this.isLoadingValueOptions = false;
      }
    },
    clearOptions() {
      this.selectOptions = [];
      this.isLoadingOptions = false;
      this.keyOptions = [];
      this.valueOptions = [];
      this.isLoadingKeyOptions = false;
      this.isLoadingValueOptions = false;
      this.lastError = null;
    },
  },
};
